/**
 * HTTP do zamrożonych endpointów §18 (profile outfit, economy purchase, me).
 * Walidacja response schematami @pieskowo/shared.
 */
import {
  OutfitPutRequestSchema,
  OutfitPutResponseSchema,
  PurchaseRequestSchema,
  PurchaseResponseSchema,
  MeResponseSchema,
  type OutfitPutRequest,
  type OutfitPutResponse,
  type PurchaseRequest,
  type PurchaseResponse,
  type MeResponse,
  ApiErrorSchema,
} from '@pieskowo/shared';
import { apiBase, authHeaders } from '../core/apiBase.js';

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: string; retryable: boolean; status: number };

async function parseError(res: Response): Promise<ApiResult<never>> {
  try {
    const json: unknown = await res.json();
    const err = ApiErrorSchema.safeParse(json);
    if (err.success) {
      return {
        ok: false,
        code: err.data.error.code,
        retryable: err.data.error.retryable,
        status: res.status,
      };
    }
  } catch {
    /* ignore */
  }
  return {
    ok: false,
    code: res.status === 401 ? 'UNAUTHORIZED' : 'INTERNAL',
    retryable: res.status >= 500,
    status: res.status,
  };
}

/** PUT /api/profile/outfit */
export async function putOutfit(
  body: OutfitPutRequest,
): Promise<ApiResult<OutfitPutResponse>> {
  const parsed = OutfitPutRequestSchema.safeParse(body);
  if (!parsed.success) {
    return { ok: false, code: 'VALIDATION', retryable: false, status: 0 };
  }
  try {
    const res = await fetch(`${apiBase()}/api/profile/outfit`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = OutfitPutResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'VALIDATION', retryable: false, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}

/** POST /api/economy/purchase */
export async function postPurchase(
  body: PurchaseRequest,
): Promise<ApiResult<PurchaseResponse>> {
  const parsed = PurchaseRequestSchema.safeParse(body);
  if (!parsed.success) {
    return { ok: false, code: 'VALIDATION', retryable: false, status: 0 };
  }
  try {
    const res = await fetch(`${apiBase()}/api/economy/purchase`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = PurchaseResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'VALIDATION', retryable: false, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}

/** GET /api/me — saldo / profil (outfit nie jest w schemacie Me). */
export async function fetchMe(): Promise<ApiResult<MeResponse>> {
  try {
    const res = await fetch(`${apiBase()}/api/me`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = MeResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'VALIDATION', retryable: false, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}

/** uuid v4 do idempotencyKey (crypto.randomUUID lub fallback). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // fallback (nie-crypto) — wystarczy unikalność kliencka
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
